/**
 * Social Media Sharing Utilities
 * Generate sharing URLs and handle social media interactions
 */

export interface ShareData {
  title: string;
  description: string;
  url: string;
  hashtags?: string[];
}

export const SOCIAL_SHARING_CONFIG = {
  baseUrl: 'https://sa-reverse-tax-calculator.com',
  defaultTitle: 'SA Reverse Tax Calculator',
  defaultDescription: 'Calculate the gross salary needed for your desired net income in South Africa. Free reverse tax calculator with current SARS tax brackets.',
  defaultHashtags: ['SATax', 'TaxCalculator', 'SouthAfrica', 'SARS', 'Salary']
};

/**
 * Generate Facebook sharing URL
 */
export function getFacebookShareUrl(data: ShareData): string {
  const params = new URLSearchParams({
    u: data.url,
    quote: `${data.title} - ${data.description}`
  });
  
  return `https://www.facebook.com/sharer/sharer.php?${params.toString()}`;
}

/**
 * Generate Twitter sharing URL
 */
export function getTwitterShareUrl(data: ShareData): string {
  const text = `${data.title} - ${data.description}`;
  const hashtags = data.hashtags?.join(',') || SOCIAL_SHARING_CONFIG.defaultHashtags.join(',');
  
  const params = new URLSearchParams({
    text,
    url: data.url,
    hashtags
  });
  
  return `https://twitter.com/intent/tweet?${params.toString()}`;
}

/**
 * Generate LinkedIn sharing URL
 */
export function getLinkedInShareUrl(data: ShareData): string {
  const params = new URLSearchParams({
    mini: 'true',
    url: data.url,
    title: data.title,
    summary: data.description
  });
  
  return `https://www.linkedin.com/sharing/share-offsite/?${params.toString()}`;
}

/**
 * Generate WhatsApp sharing URL
 */
export function getWhatsAppShareUrl(data: ShareData): string {
  const text = `${data.title} - ${data.description} ${data.url}`;
  
  const params = new URLSearchParams({
    text
  });
  
  return `https://wa.me/?${params.toString()}`;
}

/**
 * Generate email sharing URL
 */
export function getEmailShareUrl(data: ShareData): string {
  const subject = data.title;
  const body = `${data.description}

Check out this helpful tax calculator: ${data.url}

Hashtags: ${data.hashtags?.map(tag => `#${tag}`).join(' ') || SOCIAL_SHARING_CONFIG.defaultHashtags.map(tag => `#${tag}`).join(' ')}`;

  const params = new URLSearchParams({
    subject,
    body
  });
  
  return `mailto:?${params.toString()}`;
}

/**
 * Open social sharing URL in new window
 */
export function openShareWindow(url: string, platform: string): void {
  const windowFeatures = 'width=600,height=400,scrollbars=yes,resizable=yes';
  window.open(url, `share-${platform}`, windowFeatures);
}

/**
 * Copy sharing text to clipboard
 */
export async function copyToClipboard(data: ShareData): Promise<boolean> {
  const text = `${data.title} - ${data.description} ${data.url}`;
  
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    return true;
  }
}

/**
 * Use native Web Share API if available
 */
export async function nativeShare(data: ShareData): Promise<boolean> {
  if (!navigator.share) {
    return false;
  }
  
  try {
    await navigator.share({
      title: data.title,
      text: data.description,
      url: data.url
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Generate calculation-specific sharing data
 */
export function createCalculationShareData(
  grossSalary: number,
  netSalary: number,
  isMonthly: boolean = true
): ShareData {
  const period = isMonthly ? 'monthly' : 'annual';
  const formatter = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    maximumFractionDigits: 0
  });
  
  const title = 'SA Reverse Tax Calculator Results';
  const description = `I calculated that I need a ${period} gross salary of ${formatter.format(grossSalary)} to achieve my desired net income of ${formatter.format(netSalary)} in South Africa.`;
  
  return {
    title,
    description,
    url: SOCIAL_SHARING_CONFIG.baseUrl,
    hashtags: ['SATax', 'TaxCalculation', 'SouthAfrica', 'Salary', 'PAYE']
  };
}